/**
 * De zes grafiektypen van het dashboard.
 *
 * Elk type staat hier één keer, met het gedeelde thema erin verwerkt, zodat
 * een pagina alleen nog data en labels hoeft mee te geven. Dat houdt de
 * grafieken onderling gelijk en voorkomt dat er per pagina een eigen
 * variant ontstaat.
 */
import { Chart } from './Chart'
import {
  HOOGTE, KLEUREN, REEKSKLEUREN, basis, lijnReeks, mengKleur, staafReeks,
  verloop, xAs, yAs,
} from './theme'

export { HOOGTE, KLEUREN } from './theme'

/** Eén reeks met een naam, waarden en een kleur. */
export interface Reeks {
  naam: string
  waarden: number[]
  kleur?: string
}

/* ------------------------------------------------------------------- lijn */

/**
 * Lijngrafiek met verloopvulling — voor omzet per dag, reactietijd, en het
 * vergelijk tussen deze en de vorige periode.
 */
export function LineChartPanel({
  labels,
  reeksen,
  height = HOOGTE.dag,
  format = (v: number) => String(v),
  gevuld = true,
}: {
  labels: string[]
  reeksen: Reeks[]
  height?: number
  format?: (v: number) => string
  /** Zet uit bij twee reeksen die elkaar zouden overdekken. */
  gevuld?: boolean
}) {
  const leeg = !labels.length || reeksen.every((r) => r.waarden.every((v) => !v))
  return (
    <Chart
      height={height}
      empty={leeg}
      option={{
        ...basis({ format, legenda: reeksen.length > 1 }),
        xAxis: xAs(labels),
        yAxis: yAs(format),
        series: reeksen.map((r, i) =>
          lijnReeks(r.naam, r.waarden, r.kleur ?? REEKSKLEUREN[i % REEKSKLEUREN.length],
                    { gevuld: gevuld && reeksen.length === 1 })),
      }}
    />
  )
}

/* ------------------------------------------------------------------ staaf */

/** Staafgrafiek met afgeronde koppen — voor online-uren en berichten per uur. */
export function BarChartPanel({
  labels,
  reeksen,
  height = HOOGTE.uur,
  format = (v: number) => String(v),
  maxLijn,
}: {
  labels: string[]
  reeksen: Reeks[]
  height?: number
  format?: (v: number) => string
  /** Tekent de piek als lijn in plaats van als losse tekst. */
  maxLijn?: boolean
}) {
  const leeg = !labels.length || reeksen.every((r) => r.waarden.every((v) => !v))
  const piek = Math.max(0, ...reeksen.flatMap((r) => r.waarden))
  const series = reeksen.map((r, i) =>
    staafReeks(r.naam, r.waarden, r.kleur ?? REEKSKLEUREN[i % REEKSKLEUREN.length]))
  if (maxLijn && piek > 0 && series.length) {
    ;(series[0] as Record<string, unknown>).markLine = {
      silent: true,
      symbol: 'none',
      lineStyle: { color: mengKleur(KLEUREN.acc2, 0.55), type: 'dashed', width: 1 },
      label: {
        color: KLEUREN.acc2, fontSize: 10, position: 'insideEndTop',
        formatter: `max ${format(piek)}`,
      },
      data: [{ yAxis: piek }],
    }
  }
  return (
    <Chart
      height={height}
      empty={leeg}
      option={{
        ...basis({ format, legenda: reeksen.length > 1 }),
        xAxis: xAs(labels),
        yAxis: yAs(format),
        series,
      }}
    />
  )
}

/* -------------------------------------------------------------- trechter */

/** Trechter met verlooplagen — de conversie van gesprek naar verkoop. */
export function FunnelChartPanel({
  lagen,
  height = HOOGTE.trechter,
}: {
  lagen: { label: string; aantal: number; pct: number }[]
  height?: number
}) {
  return (
    <Chart
      height={height}
      empty={!lagen.length}
      option={{
        ...basis({ legenda: false }),
        grid: undefined,
        tooltip: {
          ...basis().tooltip,
          trigger: 'item',
          // 28-10-2026 (Jordy): het echte aantal, niet de vormwaarde; die is
          // bij 0 een klein getal (0.0001) zodat de laag zichtbaar blijft.
          formatter: (p: { name: string; dataIndex: number }) =>
            `${p.name}<br/><b>${lagen[p.dataIndex]?.aantal ?? 0}</b> · ${lagen[p.dataIndex]?.pct ?? 0}%`,
        },
        series: [{
          type: 'funnel',
          left: 12, right: 12, top: 10, bottom: 10,
          minSize: '24%',
          sort: 'none',
          gap: 3,
          label: {
            color: '#ece9f5', fontSize: 11, position: 'inside',
            formatter: (p: { name: string; dataIndex: number }) =>
              `${p.name} · ${lagen[p.dataIndex]?.aantal ?? 0} · ${lagen[p.dataIndex]?.pct ?? 0}%`,
          },
          itemStyle: { borderWidth: 0, borderRadius: 4 },
          data: lagen.map((l, i) => ({
            name: l.label,
            value: Math.max(l.aantal, 0.0001),
            itemStyle: {
              color: verloop(REEKSKLEUREN[i % REEKSKLEUREN.length], 0.95, 0.45),
            },
          })),
        }],
      }}
    />
  )
}

/* ------------------------------------------------------------------ donut */

/** Ringdiagram — de verdeling over platformen en modellen. */
export function DonutChartPanel({
  delen,
  height = HOOGTE.mix,
  format = (v: number) => String(v),
  midden,
}: {
  delen: { naam: string; waarde: number; kleur?: string }[]
  height?: number
  format?: (v: number) => string
  /** Tekst in het gat, bijvoorbeeld het totaal. */
  midden?: string
}) {
  return (
    <Chart
      height={height}
      empty={!delen.length || delen.every((d) => !d.waarde)}
      option={{
        ...basis({ format }),
        grid: undefined,
        tooltip: {
          ...basis({ format }).tooltip,
          trigger: 'item',
          formatter: (p: { name: string; value: number; percent: number }) =>
            `${p.name}<br/><b>${format(p.value)}</b> · ${p.percent}%`,
        },
        legend: {
          bottom: 0, icon: 'circle', itemWidth: 9, itemHeight: 9,
          textStyle: { color: '#9a92b8', fontSize: 11 },
        },
        series: [{
          type: 'pie',
          radius: ['54%', '78%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          padAngle: 2,
          itemStyle: { borderRadius: 6, borderWidth: 0 },
          label: midden
            ? {
                show: true, position: 'center', color: '#ece9f5',
                fontSize: 15, fontWeight: 700, formatter: midden,
              }
            : { show: false },
          emphasis: { scaleSize: 6, label: { show: !!midden } },
          data: delen.map((d, i) => ({
            name: d.naam,
            value: d.waarde,
            itemStyle: { color: d.kleur ?? REEKSKLEUREN[i % REEKSKLEUREN.length] },
          })),
        }],
      }}
    />
  )
}

/* -------------------------------------------------------------- sparkline */

/** Het lijntje in een kengetal-kaart: geen assen, alleen de vorm. */
export function SparkChart({
  waarden,
  kleur = KLEUREN.acc,
  height = HOOGTE.sparkline,
}: {
  waarden: number[]
  kleur?: string
  height?: number
}) {
  return (
    <Chart
      height={height}
      empty={waarden.length < 2}
      option={{
        animationDuration: 380,
        backgroundColor: 'transparent',
        grid: { left: 0, right: 0, top: 4, bottom: 0 },
        xAxis: { type: 'category', show: false, boundaryGap: false,
                 data: waarden.map((_, i) => i) },
        yAxis: { type: 'value', show: false, min: 'dataMin' },
        tooltip: { show: false },
        series: [{
          type: 'line', data: waarden, smooth: 0.4, showSymbol: false,
          lineStyle: { width: 1.8, color: kleur },
          areaStyle: { color: verloop(kleur, 0.42, 0) },
        }],
      }}
    />
  )
}

/* -------------------------------------------------------------- tegelrij */

/**
 * Gekleurde tegels per dag — de activiteit van de laatste veertien dagen.
 *
 * Geen ECharts: dit zijn veertien blokjes met een datum en een waarde, en
 * die zijn met opmaak duidelijker dan met een grafiek.
 */
export function DayTiles({
  dagen,
  format = (v: number) => String(v),
  kleur = KLEUREN.ok,
}: {
  dagen: { date: string; value: number }[]
  format?: (v: number) => string
  kleur?: string
}) {
  if (!dagen.length) {
    return <div className="chartempty" style={{ height: 96 }}>No data in this window</div>
  }
  const piek = Math.max(1, ...dagen.map((d) => d.value))
  return (
    <div className="daytiles">
      {dagen.map((d) => {
        const deel = d.value / piek
        return (
          <div
            key={d.date}
            className="tile"
            title={`${d.date}: ${format(d.value)}`}
            style={{
              background: d.value
                ? mengKleur(kleur, 0.15 + deel * 0.6)
                : 'rgba(255,255,255,.04)',
              borderColor: d.value ? mengKleur(kleur, 0.35) : 'transparent',
            }}
          >
            <span className="d">{d.date.slice(5).replace('-', '-')}</span>
            <span className="v">{d.value ? format(d.value) : '—'}</span>
          </div>
        )
      })}
    </div>
  )
}
